import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, X, MapPin, Zap, Wallet, HeartHandshake, ShieldCheck } from "lucide-react";
import DemoRequest from "../components/sections/DemoRequest";
import { useI18n } from "../i18n/I18nProvider";
import { getCompareLightspeed } from "../i18n/content";

export default function CompareLightspeed() {
  const { t, locale } = useI18n();
  const rows = getCompareLightspeed(locale);

  const VALUE_PROPS = [
    { icon: Wallet, title: t("compare_page.vp_honest_title"), body: t("compare_page.vp_honest_body") },
    { icon: Zap, title: t("compare_page.vp_focused_title"), body: t("compare_page.vp_focused_body") },
    { icon: HeartHandshake, title: t("compare_page.vp_local_title"), body: t("compare_page.vp_local_body") },
    { icon: ShieldCheck, title: t("compare_page.vp_hw_title"), body: t("compare_page.vp_hw_body") },
  ];

  return (
    <div data-testid="compare-lightspeed-page">
      <section className="relative pt-16 pb-12 sm:pt-24 sm:pb-16 border-b border-zinc-200 overflow-hidden">
        <div className="absolute inset-0 technical-grid pointer-events-none" />
        <div className="container-oj relative">
          <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white/70 backdrop-blur px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-700">
            <MapPin className="size-3.5 text-oj-brand" /> {t("compare_page.pill")}
          </div>
          <h1 className="font-display mt-6 text-balance text-5xl sm:text-7xl font-black tracking-tighter leading-[0.92] text-oj-ink max-w-4xl">
            {t("compare_page.h1_a")} <span className="bg-zinc-950 text-white px-3 rounded-md">{t("compare_page.h1_ls")}</span>.<br />
            {t("compare_page.h1_b")}
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-zinc-600 leading-relaxed">
            {t("compare_page.intro")}
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container-oj grid grid-cols-1 md:grid-cols-2 gap-5">
          {VALUE_PROPS.map((v) => (
            <article key={v.title} className="rounded-2xl border border-zinc-200 p-7 bg-zinc-50/60">
              <div className="size-10 rounded-md bg-oj-ink text-white grid place-items-center">
                <v.icon className="size-5" />
              </div>
              <h3 className="mt-5 font-display text-2xl font-bold tracking-tight text-oj-ink">{v.title}</h3>
              <p className="mt-2 text-zinc-700 leading-relaxed">{v.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="py-20 bg-zinc-50 border-t border-zinc-200">
        <div className="container-oj">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">{t("compare_page.side_by_side")}</div>
          <h2 className="font-display mt-3 text-balance text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95] text-oj-ink max-w-3xl">
            {t("compare_page.full_picture")}
          </h2>

          <div className="mt-10 rounded-2xl border border-zinc-200 bg-white overflow-hidden">
            <div className="grid grid-cols-12 px-6 py-4 border-b border-zinc-200 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">
              <div className="col-span-5">{t("compare_page.dim")}</div>
              <div className="col-span-4 text-oj-ink">O&#39;javana</div>
              <div className="col-span-3">Lightspeed</div>
            </div>
            <ul className="divide-y divide-zinc-200">
              {rows.map((row, idx) => (
                <li
                  key={idx}
                  data-testid={`compare-row-${idx}`}
                  className="grid grid-cols-12 px-6 py-4 items-start hover:bg-zinc-50/70"
                >
                  <div className="col-span-5 text-sm font-medium text-zinc-700">{row.dim}</div>
                  <div className="col-span-4 text-sm font-semibold text-oj-ink flex items-start gap-2">
                    <Check className="size-4 mt-0.5 shrink-0 text-oj-kiosk" /> {row.oj}
                  </div>
                  <div className="col-span-3 text-sm text-zinc-500 flex items-start gap-2">
                    <X className="size-4 mt-0.5 shrink-0 text-zinc-400" /> {row.ls}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-6 text-xs text-zinc-500 max-w-3xl">
            {t("compare_page.footnote")}
          </p>
        </div>
      </section>

      <section className="py-20 bg-oj-ink text-white relative overflow-hidden">
        <div className="absolute inset-0 noise opacity-30 pointer-events-none" />
        <div className="container-oj relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display text-balance text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
              {t("compare_page.bottom_h2")}
            </h2>
            <p className="mt-4 text-zinc-300 max-w-2xl">
              {t("compare_page.bottom_body")}
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link
              to="/#demo"
              data-testid="compare-bottom-demo"
              className="inline-flex items-center justify-center gap-1.5 bg-oj-brand text-zinc-950 font-semibold px-6 py-4 rounded-md hover:opacity-90"
            >
              {t("compare_page.bottom_cta")} <ArrowUpRight className="size-5" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-1.5 border border-white/20 bg-white/5 text-white font-semibold px-6 py-4 rounded-md hover:bg-white/10"
            >
              {t("compare_page.bottom_cta2")}
            </Link>
          </div>
        </div>
      </section>

      <DemoRequest />
    </div>
  );
}
